/*
 * print-documents/index.js — /print-documents.
 *
 * ЧТО ЭТОТ ТУЛ НА САМОМ ДЕЛЕ ДЕЛАЕТ
 * ─────────────────────────────────
 * Не «печатает» — печатает принтер. Он закрывает зазор между документом и
 * бумагой: собирает всё, что человек принёс, в ОДИН файл, у которого страница
 * равна листу, а порядок страниц уже такой, каким он должен выйти из лотка.
 * После этого драйверу нечего решать, и единственная настройка, которая может
 * всё испортить, — «Масштаб 100 %» — напечатана на самом листе рядом с
 * линейкой, которой её можно проверить.
 *
 * КАК УСТРОЕН ЭКРАН (переделка 23.10.2026)
 * ────────────────────────────────────────
 * Прибор — «печатный стол» в один экран: документы · листы · настройки (три
 * колонки на широком, две на ноутбуке, одна на телефоне — по ширине КОНСОЛИ,
 * а не окна). Настройки прокручиваются внутри своей колонки, а итог и кнопки
 * «Печатать / Скачать» — последняя строка той же колонки: видны всегда, без
 * липких панелей. До переделки прибор был 1650–3050 px в высоту, кнопка
 * «Скачать» стояла на третьем экране, и на 2560 px он выглядел так же, как на
 * 1920.
 *
 * ОДНА СБОРКА НА ИЗМЕНЕНИЕ
 * ────────────────────────
 * Задание собирается целиком один раз на каждое изменение настроек, и этим
 * же файлом пользуются три потребителя: просмотрщик (рисует только видимые
 * листы), замер (краска, края, пустые страницы) и выдача. Поэтому «Печатать»
 * отдаёт уже готовые байты сразу в обработчике нажатия — и браузер не успевает
 * забыть жест (см. tryPrintNow): раньше файл собирался ПОСЛЕ нажатия, и на
 * больших заданиях диалог печати не открывался вовсе.
 */

import {
    MARGIN_PRESETS, MM, selectPages, planSheets,
    duplexSplit, paperCost, unprintableRisk, sheetSize, UNPRINTABLE,
} from './impose.js';
import { buildPdf, buildEdgeTest, buildFlipTest, flipAnswerToSettings, loadPdfLib } from './build.js';
import { openSource, embedUsed, KIND } from './sources.js';
import { measurePdf, cartridgePages, isoRatio, grayscaleSaving } from './ink.js';
import { adviseJob, edgeInk, regionInk } from './advice.js';
import { looksLikeGreyScan } from './scan-clean.js';
import { createViewer } from './view.js';
import { detectDocument, quadAspect, copySizeMm, guessType } from './doc-detect.js';

const SLUG = 'print-documents';
const $ = (id) => document.getElementById(id);
const T = (k, d) => (window.PD_I18N && window.PD_I18N[k]) || d || k;
const fill = (s, map) => String(s).replace(/:(\w+)/g, (m, k) => (map[k] != null ? String(map[k]) : m));

const ae = (type, data) => { try { window.__ae && window.__ae(type, { slug: SLUG, ...data }); } catch (_) { /* */ } };
const feat = (f, data) => ae('feature_use', { f, ...data });
const stage = (n, m) => { try { window.__stage && window.__stage(n, m); } catch (_) { /* */ } };
const diag = (issue, d) => { try { window.__aeDiagnostic && window.__aeDiagnostic({ issue, ...d }); } catch (_) { /* */ } };
const isPro = () => { try { return !!(window.__tierLimits && window.__tierLimits.is_pro === true); } catch (_) { return false; } };

/* ─────────────────────────── состояние ─────────────────────────── */

const state = {
    sources: [],
    opts: {
        /* ⚠️⚠️ `preset` — ОБЪЯВЛЕНИЕ умолчания, а не ещё одна настройка, и
         * шести осей, которыми владеет пресет (`layout`, `perSheet`, `scale`,
         * `marginPreset`, `duplex`, `inkMode`), здесь НЕТ НАМЕРЕННО: они
         * приходят из `PRESETS[preset]` ниже, и второго места, где их можно
         * задать, не существует. До 08.09.2026 они стояли здесь руками, и тул
         * печатал на 88.57 % под карточкой «Реальный размер».
         * Сторож — PrintDocumentsPresetIsTheOnlyDefaultTest. */
        preset: 'exact',
        paper: 'a4',
        /* «Своя» бумага и «Свои» поля — объекты в мм. ⚠️ До 23.10.2026 их поля
         * ввода писали в `customW`/`mTop`…, которых раскладка не читала: свой
         * размер молча печатался на A4, а свои поля — как 0 мм. */
        customPaper: { w: 210, h: 297 },
        orientation: 'auto',
        margins: { top: 12, right: 12, bottom: 12, left: 12 },
        printer: 'laser',
        /* Измеренные поля этого принтера (лист-калибровка). null — не мерили. */
        printerMargins: null,
        /* Сколько миллиметров вышло у напечатанной шкалы «100 мм». 100 — драйвер
         * честен; 96 — он ужимает, и тул увеличит лист на 100/96. */
        printerScale: 100,
        copies: 1,
        nupOrder: 'row',
        nupBorder: false,
        gutter: 4,
        percent: 100,
        autoRotate: true,
        ranges: '',
        parity: 'all',
        reverse: false,
        stackOrder: 'normal',
        flip: 'long',
        posterSheetsWide: 2,
        posterOverlap: 10,
        posterMarks: true,
        bookletSignature: 0,
        bookletRtl: false,
        ruler: true,
        pageNumbers: false,
        header: '',
        footer: '',
        watermark: '',
        lighten: 0.25,
        cartridgeYield: 1500,
        pricePerSheet: 0,
        docDpi: 150,
        cleanScan: false,
    },
    plan: null,
    job: null,          // { key, bytes, sheets } — собранное задание (то, что видно)
    measure: null,      // замер ВИДИМОГО задания: краска, края, пустые ячейки
    rawMeasure: null,   // { key, m } — замер без режима краски (по нему ищутся тёмные листы)
    built: null,        // последний выданный файл (для запасных дверей печати)
    printOk: null,
    busy: false,
    flipTested: false,
    dismissed: new Set(),
    noCleanOnce: false,
};

/* Потолок копий: выше правильный ответ — копии в диалоге печати. */
const COPIES_MAX = 50;

/*
 * Умолчание тула — «Реальный размер» (1:1, без полей). Пресетов как карточек
 * на экране больше нет (23.10.2026): шесть карточек дублировали четыре вида
 * раскладки и масштаб — один и тот же выбор жил в двух местах. Таблица
 * осталась как ОБЪЯВЛЕНИЕ умолчания и как ярлык в телеметрии («что люди
 * собирают»): `currentPreset()` выводит его из настроек.
 */
const PRESETS = {
    exact: { layout: 'single', perSheet: 1, scale: 'actual', marginPreset: 'none', duplex: 'off', inkMode: 'none' },
    fit: { layout: 'single', perSheet: 1, scale: 'shrink', marginPreset: 'normal', duplex: 'off', inkMode: 'none' },
    save: { layout: 'nup', perSheet: 2, scale: 'fit', marginPreset: 'narrow', duplex: 'manual', inkMode: 'none' },
    draft: { layout: 'nup', perSheet: 4, scale: 'fit', marginPreset: 'narrow', duplex: 'manual', inkMode: 'lighten' },
    booklet: { layout: 'booklet', perSheet: 1, scale: 'shrink', marginPreset: 'narrow', duplex: 'manual', inkMode: 'none' },
    poster: { layout: 'poster', perSheet: 1, scale: 'fit', marginPreset: 'narrow', duplex: 'off', inkMode: 'none' },
};

/* Умолчания приводятся к ОБЪЯВЛЕННОМУ пресету здесь, а не руками в литерале. */
Object.assign(state.opts, PRESETS[state.opts.preset]);

/*
 * Что помнится между визитами — СВОЙСТВА МАШИНЫ И ЧЕЛОВЕКА, а не задания.
 * Бумага в лотке, тип принтера, его поля, его поправка масштаба, ресурс
 * картриджа, привычка печатать линейку. Раскладка, поля, копии, двусторонняя —
 * это задание: запомненный «Плакат» превратил бы следующий договор в 36
 * листов. До 23.10.2026 запоминалось ВСЁ, к чему прикасались, а ответ теста
 * переворота лежал под тем же ключом `duplex`, что и выбор «С двух сторон», —
 * после теста режим восстанавливался объектом, и ни одна кнопка не горела.
 */
const PERSIST = ['paper', 'customPaper', 'printer', 'printerMargins', 'printerScale', 'cartridgeYield', 'docDpi', 'ruler'];

/* ─────────────────────────── приём файлов ─────────────────────────── */

async function addFiles(files, how = 'pick') {
    const list = [...files].filter(Boolean);
    if (!list.length) return;
    stage('files_adding', { n: list.length, how });
    setStageBusy(true, T('busy_reading', 'Reading files…'));

    for (const file of list) {
        try {
            const src = await openSource(file, {
                docDpi: state.opts.docDpi,
                onProgress: (p) => setStageBusy(true, T('busy_reading', 'Reading files…'), p),
            });
            src.range = '';
            src.enabled = true;
            /* Ключ лимита штампуется ОДИН раз при добавлении и живёт в записи:
             * тул может подменить файл (HEIC → JPEG), и та же бумага стала бы
             * «новым файлом» на втором экспорте. */
            src.limitKey = `${file.name}|${file.size}|${file.lastModified}`;
            state.sources.push(src);
            feat('file_added', { kind: src.kind, pages: src.pages.length, how });
        } catch (e) {
            const code = e && e.code ? e.code : 'OPEN_FAIL';
            diag(code, { name: file.name });
            window.__toast(fileError(code, file.name), 'error');
        }
    }
    setStageBusy(false);
    renderFiles();
    syncZone();
    recompute({ now: true });
    /* Штамп ЭЦП и страницы-сканы ищем ПОСЛЕ пересчёта и не ждём: это
     * подсказки, а не условие работы. */
    for (const src of state.sources) inspectPdfSource(src).catch(() => { /* */ });
    for (const src of state.sources) findDocument(src).catch(() => { /* */ });
}

function fileError(code, name) {
    const map = {
        UNSUPPORTED_TYPE: T('err_unsupported', 'This file type cannot be printed here'),
        PDF_UNREADABLE: T('err_pdf_broken', 'This PDF is damaged and cannot be opened'),
        PDF_BROKEN_TREE: T('err_pdf_broken', 'This PDF is damaged and cannot be opened'),
        PDF_NO_PAGES: T('err_pdf_empty', 'This PDF has no pages'),
        DOC_UNREADABLE: T('err_doc_broken', 'This Word file cannot be read'),
        DOCX_TOO_MANY_PAGES: T('err_doc_huge', 'This document is too long'),
        HEIC_UNAVAILABLE: T('err_heic', 'Could not convert the iPhone photo'),
    };
    return `${name}: ${map[code] || T('err_generic', 'Could not open this file')}`;
}

/*
 * Строка зоны загрузки («файл +N · Другой файл · ×») — общий компонент, и
 * прятать зону руками нельзя (UploaderLoadedStateConventionTest). Тул только
 * держит строку в правде: после любого добавления она называет ВЕСЬ набор,
 * а когда крестиками в списке убран последний файл — зона возвращается в
 * исходный вид тем же путём, что её собственный ×.
 */
function zoneEl() { return document.getElementById('pdDrop'); }
function syncZone() {
    const z = zoneEl();
    if (!z) return;
    if (state.sources.length) {
        if (typeof z.__uploaderCollapse === 'function') z.__uploaderCollapse(state.sources.map((s) => s.file).filter(Boolean));
    } else if (z.classList.contains('tu-collapsed')) {
        const row = z.__tuRow || z.querySelector('.tu-picked');
        const x = row && row.querySelector('.tu-picked-remove');
        if (x) x.click();
    }
}

/** Файлы не из зоны (бросок на стол, вставка) — через ту же дверь зоны: её проверки размера и типа, её телеметрия. */
function injectFiles(files, how) {
    const z = zoneEl();
    if (z && typeof z.__uploaderInjectFiles === 'function') { z.__uploaderInjectFiles(files, how); return; }
    addFiles(files, how);
}

function clearAll() {
    for (const s of state.sources) { try { s.close && s.close(); } catch (_) { /* */ } }
    state.sources = [];
    state.dismissed.clear();
    renderFiles();
    recompute({ now: true });
}

/* ─────────────────────────── расчёт ─────────────────────────── */

function activePages() {
    const out = [];
    /* Исключённые страницы помнит САМ ИСТОЧНИК (`s.excluded`): индекс
     * источника съезжает, как только удалят соседний файл. Страница без
     * своего размера (docx без `w:pgSz`) печатается на ВЫБРАННОЙ бумаге. */
    const [defW, defH] = sheetSize(state.opts.paper, 'portrait', state.opts.customPaper);
    state.sources.forEach((s, si) => {
        if (!s.enabled) return;
        const sel = selectPages({ total: s.pages.length, ranges: s.range || '', parity: 'all', reverse: false });
        for (const pi of sel.pages) {
            if (s.excluded && s.excluded.has(pi)) continue;
            const src = s.pages[pi];
            out.push({ w: src.w == null ? defW : src.w, h: src.h == null ? defH : src.h, loose: !!src.loose, ref: `${si}:${pi}`, src: si, page: pi });
        }
    });
    return out;
}

function marginsFor(o) {
    return o.marginPreset === 'custom' ? o.margins : o.marginPreset;
}

function buildPlan() {
    const o = state.opts;
    let pages = activePages();
    if (!pages.length) return null;

    const sel = selectPages({ total: pages.length, ranges: o.ranges, parity: o.parity, reverse: o.reverse });
    pages = sel.pages.map((i) => pages[i]);
    if (!pages.length) return null;

    const baseMargins = marginsFor(o);
    const lay = (margins) => {
        const plan = planSheets({
            pages, paper: o.paper, orientation: o.orientation, margins,
            custom: o.paper === 'custom' ? o.customPaper : null,
            layout: o.layout, perSheet: o.perSheet, nupOrder: o.nupOrder,
            nupBorder: o.nupBorder, gutter: o.gutter, scale: o.scale,
            percent: o.percent, autoRotate: o.autoRotate,
            /* Допуск обрезки = то, что принтер потеряет у края в любом случае. */
            clipTolerance: unprintableRisk(margins, printerProfile(o)).worst || 0.05,
            poster: { sheetsWide: o.posterSheetsWide, overlap: o.posterOverlap, marks: o.posterMarks },
            booklet: { perSignature: o.bookletSignature, rtl: o.bookletRtl },
        });
        plan.dropped = sel.dropped;
        plan.bad = sel.bad;
        return plan;
    };

    const plain = lay(baseMargins);
    if (!o.ruler) { plain.rulerRoom = null; return plain; }

    /* ⚠️⚠️ ЛИНЕЙКА — СВИДЕТЕЛЬ, А НЕ УЧАСТНИК: она не имеет права двигать
     * документ. Полоса под неё (~14 мм) берётся, только если раскладка с ней
     * не дороже раскладки без неё (масштаб не упал, обрезки и листов не
     * прибавилось). Иначе линейки нет, и причина сказана у выключателя. */
    const m = typeof baseMargins === 'string' ? { ...MARGIN_PRESETS[baseMargins] } : { ...(baseMargins || {}) };
    const u = profileMm(o);
    m.bottom = Math.max(14 + Math.max(0, (u.bottom || 0) - 4.5), Number(m.bottom) || 0);
    const withRuler = lay(m);

    if (!costsTheDocument(plain, withRuler)) { withRuler.rulerRoom = true; return withRuler; }
    plain.rulerRoom = false;
    return plain;
}

/** Раскладка `b` дороже для документа, чем `a`? */
function costsTheDocument(a, b) {
    const worst = (plan) => {
        let scale = Infinity, clipped = 0;
        for (const sh of plan.sheets || []) {
            for (const sl of sh.slots || []) {
                if (typeof sl.scale === 'number') scale = Math.min(scale, sl.scale);
                if (sl.clipped) clipped++;
            }
        }
        return { scale, clipped, sheets: (plan.sheets || []).length };
    };
    const x = worst(a), y = worst(b);
    return y.sheets > x.sheets || y.clipped > x.clipped || y.scale < x.scale - 1e-9;
}

/** Профиль принтера: измеренный, если мерили, иначе тип из таблицы. */
function printerProfile(o) {
    const m = o.printerMargins;
    if (m && ['top', 'right', 'bottom', 'left'].every((k) => Number.isFinite(+m[k]))) return m;
    return o.printer;
}
function profileMm(o) {
    const p = printerProfile(o);
    return typeof p === 'object' ? p : (UNPRINTABLE[p] || UNPRINTABLE.laser);
}

/** Поправка масштаба принтера: шкала 100 мм вышла v мм → лист × 100/v. */
function scaleFix(o) {
    const v = Number(o.printerScale);
    if (!(v >= 80 && v <= 120)) return 1;
    return 100 / v;
}

/* Ключи: что меняет СОДЕРЖИМОЕ собранного файла. Копии и ручной дуплекс
 * меняют только порядок и число листов при выдаче — предпросмотру и замеру
 * они не нужны. */
function sourcesKey() {
    return state.sources.map((s) => [s.limitKey, s.enabled ? 1 : 0, s.range || '', s.excluded ? [...s.excluded].sort().join('.') : '', s.cropKey || ''].join('~')).join('|');
}
function contentKey() {
    const o = state.opts;
    const pick = { ...o };
    for (const k of ['inkMode', 'lighten', 'copies', 'duplex', 'stackOrder', 'flip', 'cartridgeYield', 'pricePerSheet', 'preset']) delete pick[k];
    return sourcesKey() + '#' + JSON.stringify(pick);
}
function viewKey() {
    const o = state.opts;
    return contentKey() + `#${o.inkMode}:${o.inkMode === 'lighten' ? o.lighten : ''}`;
}

let recomputeTimer = null;
function recompute({ now = false } = {}) {
    state.plan = buildPlan();
    // Замер относится к КОНКРЕТНОЙ сборке: поменялось содержимое — прежние
    // проценты, края и пустые ячейки уже не про этот файл.
    if (!state.job || state.job.key !== viewKey()) state.measure = null;
    renderSummary();
    renderModes();
    $('pdRulerSkipped').hidden = !(state.opts.ruler && state.plan && state.plan.rulerRoom === false);
    setDeskState();
    clearTimeout(recomputeTimer);
    // Сборка — через короткую паузу: пока человек крутит регулятор или
    // набирает «1-3, 7», собирать нечего. Подсказки и сводка — сразу.
    recomputeTimer = setTimeout(() => { refreshJob().catch((e) => diag('job_fail', { message: String(e && e.message).slice(0, 140) })); }, now ? 0 : 180);
    renderTips();
}

/* ─────────────────────────── сборка ─────────────────────────── */

let pdfjs = null;
async function ensurePdfjs() {
    if (pdfjs) return pdfjs;
    pdfjs = await window.__pdfjs(window.PD_PDFJS_SOURCES || []);
    return pdfjs;
}

/**
 * Собрать задание.
 * @param plan        раскладка (по умолчанию текущая)
 * @param output      true — для выдачи: копии и разбивка ручного дуплекса
 *                    на лицо/оборот; false — для экрана: порядок плана.
 * @param clean       вкладывать ли очищенные сканы
 * @param darkSheets  Set индексов плана, которые инвертировать (режим «инверсия тёмных»)
 */
async function assemble({ plan = state.plan, output = false, clean = state.opts.cleanScan, darkSheets = null, onProgress } = {}) {
    if (!plan || !plan.sheets.length) return null;
    const PDFLib = await loadPdfLib();
    void PDFLib;
    const o = state.opts;

    // Каждому листу — его номер в плане: по нему режим краски находит лист
    // после того, как дуплекс и копии переставят порядок.
    const tagged = plan.sheets.map((s, i) => ({ ...s, planIndex: i }));
    let sheets = tagged;
    let duplex = null;
    if (output && o.duplex === 'manual' && o.layout !== 'poster') {
        duplex = duplexSplit(tagged, { stackOrder: o.stackOrder, flip: o.flip });
        sheets = duplex.fronts.concat(duplex.backs);
    }
    /* ── КОПИИ ────────────────────────────────────────────────────────────
     * Повторяется ВЕСЬ комплект (1,2,3,1,2,3) — «разобрать по копиям». При
     * ручном дуплексе — каждая половина отдельно: все лица N раз, потом все
     * обороты N раз, иначе вторая копия печаталась бы на обороте первой.
     * Копии не берутся из капа: это та же работа, за которую уже списано. */
    const copies = output ? Math.max(1, Math.min(COPIES_MAX, Number(o.copies) || 1)) : 1;
    if (copies > 1) {
        const times = (arr) => { const out = []; for (let i = 0; i < copies; i++) out.push(...arr); return out; };
        if (duplex) {
            duplex = { ...duplex, fronts: times(duplex.fronts), backs: times(duplex.backs) };
            sheets = duplex.fronts.concat(duplex.backs);
        } else {
            sheets = times(sheets);
        }
    }
    const work = { ...plan, sheets };

    const refs = [];
    for (const sh of sheets) for (const sl of sh.slots) if (refs.indexOf(sl.ref) < 0) refs.push(sl.ref);
    const lib = clean ? await ensurePdfjs() : null;

    const inkFor = o.inkMode === 'lighten'
        ? () => 'lighten'
        : (o.inkMode === 'invert-dark' && darkSheets ? (sh) => (darkSheets.has(sh.planIndex) ? 'invert' : null) : null);

    const bytes = await buildPdf(work, (outDoc) => embedUsed(outDoc, state.sources, refs, { clean, pdfjs: lib, onProgress }), {
        ruler: o.ruler && plan.rulerRoom !== false,
        rulerLabel: T('ruler_label', '100 mm — check with a ruler. If it differs, set Scale = 100% (Actual size).'),
        pageNumbers: o.pageNumbers,
        header: o.header || '',
        footer: o.footer || '',
        watermark: o.watermark || '',
        title: T('job_title', 'Print job'),
        safe: profileMm(o),
        scaleFix: scaleFix(o),
        inkFor,
        lighten: o.lighten,
    });
    return { bytes, sheets: sheets.length, duplex, plan: work };
}

let jobToken = 0;
let measureTimer = null;

/**
 * Пересобрать то, что видно: файл → просмотрщик → (в фоне) замер.
 */
async function refreshJob() {
    const token = ++jobToken;
    const plan = state.plan;
    if (!plan || !plan.sheets.length) {
        state.job = null; state.measure = null;
        viewer.clear();
        renderInk(); renderTips();
        return;
    }
    const o = state.opts;
    const key = viewKey();
    if (state.job && state.job.key === key && viewer.count) return;   // ничего не поменялось
    const alive = () => token === jobToken;

    const cleaning = o.cleanScan && hasCleanable();
    let cleaned = 0;
    setStageBusy(true, cleaning ? T('busy_cleaning', 'Cleaning scans…') : T('busy_building', 'Building the print file…'));
    try {
        const lib = await ensurePdfjs();
        let darkSheets = null;
        if (o.inkMode === 'invert-dark') {
            /* Тёмные листы ищутся на сборке БЕЗ инверсии: замер инвертированного
             * файла назвал бы светлыми именно их. Замер кэшируется по
             * содержимому — переключение «Бледнее ↔ Инверсия» его не теряет. */
            const ck = contentKey();
            if (!state.rawMeasure || state.rawMeasure.key !== ck) {
                const raw = await assemble({ plan });
                if (!alive()) return;
                const m = await measurePdf(lib, raw.bytes, { dpi: 30, maxPages: 120, alive });
                if (!alive()) return;
                state.rawMeasure = { key: ck, m };
            }
            darkSheets = new Set(state.rawMeasure.m.darkPages.map((p) => p - 1));
        }
        const built = await assemble({
            plan, darkSheets,
            onProgress: () => { cleaned++; setStageBusy(true, fill(T('busy_cleaning_n', 'Cleaning scans… :n'), { n: cleaned })); },
        });
        if (!alive() || !built) return;
        const doc = await lib.getDocument({ data: built.bytes.slice(0) }).promise;
        if (!alive()) { try { doc.destroy(); } catch (_) { /* */ } return; }
        state.job = { key, bytes: built.bytes, sheets: built.sheets, plan, darkSheets };
        state.measure = null;
        viewer.setJob({
            doc, plan, mode: viewMode(), profile: profileMm(o),
            caption: cardCaption, faceTag, dropFor,
        });
        setStageBusy(false);
        renderInk();
        clearTimeout(measureTimer);
        measureTimer = setTimeout(() => { measureJob(token, doc).catch(() => { /* */ }); }, 350);
    } catch (e) {
        if (alive()) {
            setStageBusy(false);
            diag('preview_fail', { message: String(e && e.message).slice(0, 140) });
            /* Молчаливый пустой стол хуже отказа: человек ждал листы. */
            window.__handleError(e, 'print_documents_preview', { say: T('err_build', 'Could not build the print file.') });
        }
    }
}

/** Замер того, что видно: краска по листам, краска в недостижимой полосе, пустые ячейки. */
async function measureJob(token, doc) {
    const alive = () => token === jobToken;
    const lib = await ensurePdfjs();
    const plan = state.plan;
    const o = state.opts;
    const u = profileMm(o);
    const t0 = performance.now();
    const m = await measurePdf(lib, null, {
        doc, dpi: 36, maxPages: 80, alive,
        inspect(pageNo, img, pxPerPt) {
            const sh = plan.sheets[pageNo - 1];
            if (!sh) return {};
            const pxPerMm = pxPerPt * MM;
            const H = img.height;
            const rects = (sh.slots || []).map((sl) => ({ x: sl.x * pxPerPt, y: H - (sl.y + sl.h) * pxPerPt, w: sl.w * pxPerPt, h: sl.h * pxPerPt }));
            const slots = (sh.slots || []).map((sl, k) => ({ ref: sl.ref, ink: regionInk(img, rects[k]) }));
            const edge = o.layout === 'poster' ? null : edgeInk(img, pxPerMm, u, { rects });
            return { edge, slots };
        },
    });
    if (!alive()) return;
    state.measure = m;
    if (o.inkMode === 'none') state.rawMeasure = { key: contentKey(), m };
    feat('ink_measured', {
        mono: Math.round(((m.avg && m.avg.mono) || 0) * 1000) / 10,
        dark: m.darkPages.length, ms: Math.round(performance.now() - t0), n: m.sampled,
    });
    renderInk();
    renderSummary();
    renderTips();
}

function viewMode() {
    const o = state.opts;
    if (o.layout === 'poster') return 'poster';
    if (o.layout === 'booklet' || o.duplex !== 'off') return 'pairs';
    return 'single';
}

function cardCaption(card, ci) {
    const plan = state.plan;
    if (card.poster) {
        const sh = plan.sheets[card.faces[0]];
        const sl = sh && sh.slots && sh.slots[0];
        const cm = sl ? `${Math.round((sl.w / MM) / 10)}×${Math.round((sl.h / MM) / 10)}` : '';
        return fill(T('cap_poster', 'Poster :n · :cols×:rows sheets · :size cm'), { n: ci + 1, cols: card.cols, rows: card.rows, size: cm });
    }
    return fill(T('cap_sheet', 'Sheet :n'), { n: ci + 1 });
}

function faceTag(card, si) {
    if (!card) return '';
    if (card.poster) {
        const sh = state.plan.sheets[si];
        return sh && sh.poster ? sh.poster.label : '';
    }
    if (card.pair) return card.faces[0] === si ? T('tag_front', 'front') : T('tag_back', 'back');
    return '';
}

/** У какой грани есть кнопка «не печатать»: только у грани с одной страницей. */
function dropFor(si) {
    const sh = state.plan && state.plan.sheets[si];
    const slots = sh && sh.slots ? sh.slots.filter((s) => s && s.ref) : [];
    if (slots.length !== 1 || state.opts.layout === 'poster') return null;
    const [src, page] = String(slots[0].ref).split(':').map(Number);
    return { src, page };
}

/* ─────────────────────────── чистый скан ─────────────────────────── */

function hasCleanable() {
    return state.sources.some((s) => s.enabled && (s.kind === KIND.IMAGE || (s.kind === KIND.PDF && s.pages.some((_, i) => s.cleanable(i) === true))));
}

/** Сколько листов задания похожи на серый скан, который стоит почистить. */
function cleanableSheets() {
    const m = state.measure;
    if (!m || !state.plan) return 0;
    let n = 0;
    for (const s of m.pages || []) {
        const sh = state.plan.sheets[s.page - 1];
        if (!sh || !looksLikeGreyScan(s)) continue;
        const any = (sh.slots || []).some((sl) => {
            const [si, pi] = String(sl.ref).split(':').map(Number);
            const src = state.sources[si];
            return src && (src.kind === KIND.IMAGE || (src.kind === KIND.PDF && src.cleanable(pi) === true));
        });
        if (any) n++;
    }
    return n;
}

/** Штамп ЭЦП в хвосте и страницы без текста (сканы) — у PDF, фоном. */
const ECP_STAMP = /(ПОДПИСАН[А-ЯЁ]*\s+)?(УСИЛЕННОЙ\s+)?(КВАЛИФИЦИРОВАННОЙ\s+)?ЭЛЕКТРОННОЙ\s+ПОДПИСЬЮ/i;
const ECP_SUPPORT = /(СЕРТИФИКАТ|ВЛАДЕЛЕЦ|ДЕЙСТВИТЕЛЕН\s+С)/i;
/* Лист-штамп — это несколько строк: 176 символов у штампа против 1900+ у
 * содержательной страницы договора. 600 — с большим запасом. */
const ECP_MAX_CHARS = 600;
/* Разбор требует второй копии байтов (первая у pdf-lib): книга в 300 МБ ради
 * подсказки вкладку убьёт. */
const INSPECT_MAX_FILE_MB = 60;
/* Сколько страниц от начала проверяем на «скан ли это»: дальше — лениво,
 * когда «Чистый скан» действительно включён и страница идёт в печать. */
const SCAN_PROBE_PAGES = 30;

async function inspectPdfSource(src) {
    if (src.kind !== KIND.PDF || src.inspected) return;
    if (src.file && src.file.size > INSPECT_MAX_FILE_MB * 1024 * 1024) return;
    src.inspected = true;
    try {
        const lib = await ensurePdfjs();
        const n = src.pages.length;
        /* ── ЭЦП: смотрим ХВОСТ, где штамп и ставят. ⚠️ Нужны ОБА условия:
         * каноническая формулировка И почти пустой лист — в договоре та же
         * фраза бывает внутри пункта. Единственную страницу не предлагаем. */
        if (n >= 2) {
            const found = [];
            for (let i = Math.max(0, n - 3); i < n; i++) {
                const text = await src.textOf(i, lib);
                if (text.length <= ECP_MAX_CHARS && ECP_STAMP.test(text) && ECP_SUPPORT.test(text)) found.push(i);
            }
            if (found.length && found.length < n) {
                src.ecpPages = found;
                feat('ecp_found', { n: found.length, of: n });
            }
        }
        for (let i = 0; i < Math.min(n, SCAN_PROBE_PAGES); i++) await src.textOf(i, lib);
        renderTips();
        renderInk();
    } catch (e) {
        diag('pdf_inspect_fail', { message: String(e && e.message).slice(0, 100) });
    }
}

/* ─────────────────────────── сводка ─────────────────────────── */

const plural = (n, base, d) => {
    let cat = 'other';
    try { cat = new Intl.PluralRules(window.__intlLocale ? window.__intlLocale() : undefined).select(n); } catch (_) { /* */ }
    return T(`${base}_${cat}`, T(`${base}_other`, d));
};
const paperLabel = (k) => (k === 'custom' ? T('paper_custom', 'Custom') : k.toUpperCase());
const fmtPct = (v) => {
    const x = Math.round(v * 1000) / 10;
    try { return new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : undefined, { maximumFractionDigits: 1 }).format(x); } catch (_) { return String(x); }
};

function renderSummary() {
    const has = !!(state.plan && state.plan.sheets.length);
    const o = state.opts;
    const num = $('pdSumSheets'), lab = $('pdSumSheetsL'), sub = $('pdSumSub');
    for (const b of document.querySelectorAll('[data-produce]')) b.disabled = state.busy || !has;
    if (!has) {
        num.textContent = '0';
        lab.textContent = T('sum_empty', 'Add files — the sheet count appears here');
        sub.textContent = '';
        return;
    }
    const cost = paperCost(state.plan.stats, { duplex: o.duplex !== 'off', pricePerSheet: o.pricePerSheet });
    const copies = Math.max(1, Math.min(COPIES_MAX, Number(o.copies) || 1));
    const sheets = cost.sheets * copies;
    num.textContent = String(sheets);
    const paper = sheetSize(o.paper, state.plan.stats.orient, o.customPaper);
    lab.textContent = fill(plural(sheets, 'sum_sheets', ':paper sheets'), { paper: paperLabel(o.paper) });
    lab.title = `${paperLabel(o.paper)} · ${Math.round(paper[0])}×${Math.round(paper[1])} ${T('unit_mm', 'mm')}`;
    const bits = [fill(T('sum_pages', ':n pages'), { n: state.plan.stats.pages })];
    if (copies > 1) bits.push(fill(T('sum_copies', ':n copies'), { n: copies }));
    if (state.measure && state.measure.avg) bits.push(fill(T('sum_ink', 'ink :p %'), { p: fmtPct(state.measure.avg.mono || 0) }));
    if (cost.saved > 0) bits.push(fill(T('sum_saved', 'paper −:p %'), { p: cost.savedPct }));
    if (Math.abs(scaleFix(o) - 1) > 0.0005) bits.push(fill(T('sum_scalefix', 'scale ×:k'), { k: scaleFix(o).toFixed(3) }));
    sub.textContent = bits.join(' · ');
}

/* ─────────────────────────── подсказки ─────────────────────────── */

let lastTipKey = null;
function renderTips() {
    const el = $('pdTips');
    if (!el) return;
    const o = state.opts;
    const tips = state.plan ? adviseJob({
        plan: state.plan, opts: o, sources: state.sources, measure: state.measure,
        profile: profileMm(o), flipTested: state.flipTested, dismissed: state.dismissed,
        cleanable: o.cleanScan ? 0 : cleanableSheets(),
        docPhoto: docPhotoCandidate(),
    }) : [];

    const edge = tips.find((t) => t.code === 'edge_ink');
    viewer.markEdges(edge ? edge.sheets.map((p) => p - 1) : []);

    el.textContent = '';
    for (const t of tips) {
        const div = document.createElement('div');
        div.className = `pd-tip pd-tip-${t.level}`;
        div.dataset.code = t.code;
        const ico = document.createElement('span');
        ico.className = 'pd-tip-ico';
        ico.setAttribute('aria-hidden', 'true');
        div.appendChild(ico);
        const body = document.createElement('div');
        body.className = 'pd-tip-body';
        const p = document.createElement('p');
        p.textContent = fill(T(`tip_${t.code}`, t.code), t.vars || {});
        body.appendChild(p);
        if (t.fixes && t.fixes.length) {
            const row = document.createElement('div');
            row.className = 'pd-tip-acts';
            for (const f of t.fixes) {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'tc-btn sm' + (t.level === 'tip' ? '' : ' ghost');
                b.textContent = fill(T(f.label, f.id), f.vars || {});
                if (f.pro && !isPro() && window.PD_PRO_OFFERED !== false) {
                    const badge = document.createElement('span');
                    badge.className = 'tc-perk';
                    badge.textContent = window.PD_PLAN || 'Premium';
                    b.appendChild(document.createTextNode(' '));
                    b.appendChild(badge);
                }
                b.addEventListener('click', () => { feat('tip_apply', { code: t.code, fix: f.id }); applyFix(f.id, t); });
                row.appendChild(b);
            }
            body.appendChild(row);
        }
        div.appendChild(body);
        if (t.level === 'tip' || t.level === 'info') {
            const x = document.createElement('button');
            x.type = 'button';
            x.className = 'pd-tip-x';
            x.textContent = '×';
            x.title = T('tip_dismiss', 'Hide this tip');
            x.setAttribute('aria-label', x.title);
            x.addEventListener('click', () => { state.dismissed.add(t.code); feat('tip_dismiss', { code: t.code }); renderTips(); });
            div.appendChild(x);
        }
        el.appendChild(div);
    }
    el.hidden = !tips.length;

    /* Событие — только когда НАБОР кодов изменился: renderTips зовётся на
     * каждое нажатие в поле диапазона. */
    const key = tips.map((i) => i.code).sort().join(',');
    if (key !== lastTipKey) {
        lastTipKey = key;
        if (key) feat('warn_shown', { codes: key });
    }
}

function applyFix(id, tip) {
    const o = state.opts;
    switch (id) {
        case 'margins_normal': setOpts({ marginPreset: 'normal' }); break;
        case 'fewer_per_sheet': setOpts({ perSheet: o.perSheet > 4 ? 4 : 2 }); break;
        case 'overlap_default': setOpts({ posterOverlap: 10 }); break;
        case 'scale_shrink': setOpts({ scale: 'shrink' }); break;
        case 'to_poster': setOpts({ layout: 'poster', marginPreset: 'narrow', scale: 'fit' }); break;
        case 'fit_printable': {
            /* Минимальное уменьшение, при котором всё уходит в печатную область:
             * поля = то, что принтер не достаёт (+0.5 мм), масштаб «вписать». */
            const u = profileMm(o);
            const r = (v) => Math.ceil(((+v || 0) + 0.5) * 2) / 2;
            setOpts({ marginPreset: 'custom', margins: { top: r(u.top), right: r(u.right), bottom: r(u.bottom), left: r(u.left) }, scale: o.layout === 'single' ? 'shrink' : o.scale });
            break;
        }
        case 'drop_blank': {
            for (const ref of tip.refs || []) {
                const [si, pi] = String(ref).split(':').map(Number);
                const s = state.sources[si];
                if (s) (s.excluded || (s.excluded = new Set())).add(pi);
            }
            feat('blank_dropped', { n: (tip.refs || []).length });
            renderFiles();
            recompute();
            break;
        }
        case 'drop_ecp': {
            for (const x of state.sources) {
                if (!x.ecpPages) continue;
                for (const i of x.ecpPages) (x.excluded || (x.excluded = new Set())).add(i);
            }
            renderFiles();
            recompute();
            break;
        }
        case 'pack_small': setOpts({ layout: 'nup', perSheet: tip.vars.per, scale: 'actual', marginPreset: 'narrow' }); break;
        case 'two_up': setOpts({ layout: 'nup', perSheet: 2, scale: 'fit', marginPreset: 'narrow' }); break;
        case 'poster_fewer': setOpts({ posterSheetsWide: Math.max(1, o.posterSheetsWide - 1) }); break;
        case 'first_page': setOpts({ ranges: '1' }); break;
        case 'invert_dark': setOpts({ inkMode: 'invert-dark' }); openDetails('pdDetInk'); break;
        case 'clean_scan': setOpts({ cleanScan: true }); feat('clean_toggle', { on: 1, via: 'tip' }); break;
        case 'flip_test': openFlipTest(); break;
        case 'crop_doc': if (tip.vars && tip.vars.src != null) openCrop(tip.vars.src, 'tip'); break;
        default: break;
    }
}

function openDetails(id) {
    const d = $(id);
    if (d && !d.open) d.open = true;
}

/* ─────────────────────────── список файлов ─────────────────────────── */

function renderFiles() {
    const el = $('pdFiles');
    if (!el) return;
    el.textContent = '';
    let pages = 0;
    state.sources.forEach((s, i) => {
        pages += s.pages.length;
        const li = document.createElement('li');
        li.className = 'pd-file';
        li.dataset.kind = s.kind;
        li.innerHTML = `
            <span class="pd-file-ico" aria-hidden="true"></span>
            <span class="pd-file-text"><span class="pd-file-name"></span><span class="pd-file-meta"></span><button type="button" class="pd-file-crop" data-crop="${i}" hidden></button></span>
            <input class="tc-in pd-file-range" type="text" inputmode="numeric" autocomplete="off" data-range="${i}">
            <span class="pd-file-tools">
                <button type="button" class="pd-ib" data-move="${i}:-1"></button>
                <button type="button" class="pd-ib" data-move="${i}:1"></button>
                <button type="button" class="pd-ib pd-ib-x" data-drop="${i}"></button>
            </span>`;
        li.querySelector('.pd-file-name').textContent = s.name;
        li.querySelector('.pd-file-meta').textContent = fileMeta(s);
        if (s.kind === KIND.IMAGE) {
            const cb = li.querySelector('.pd-file-crop');
            cb.hidden = false;
            cb.textContent = s.crop ? T('crop_edit', 'Document crop: edit') : T('crop_open', 'Crop to the document');
        }
        const r = li.querySelector('.pd-file-range');
        r.value = s.range || '';
        r.placeholder = T('range_ph', 'all');
        r.setAttribute('aria-label', fill(T('range_aria', 'Pages of :name'), { name: s.name }));
        r.title = T('files_range_hint', '');
        const [up, down, x] = li.querySelectorAll('.pd-ib');
        up.textContent = '↑'; down.textContent = '↓'; x.textContent = '×';
        up.title = T('move_up', 'Move up'); down.title = T('move_down', 'Move down'); x.title = T('remove', 'Remove');
        for (const b of [up, down, x]) b.setAttribute('aria-label', `${b.title}: ${s.name}`);
        up.disabled = i === 0;
        down.disabled = i === state.sources.length - 1;
        el.appendChild(li);
    });
    const has = state.sources.length > 0;
    $('pdFilesBox').hidden = !has;
    const meta = $('pdDocsMeta');
    if (meta) meta.textContent = has ? fill(T('docs_meta', ':files · :pages'), {
        files: `${state.sources.length} ${plural(state.sources.length, 'files', 'files')}`,
        pages: `${pages} ${T('page_many', 'pp.')}`,
    }) : '';
    const dropped = state.sources.reduce((k, x) => k + (x.excluded ? x.excluded.size : 0), 0);
    $('pdDroppedRow').hidden = dropped === 0;
    $('pdDroppedText').textContent = fill(T('sheets_dropped', 'Removed from the job: :n'), { n: dropped });
    setDeskState();
}

function fileMeta(s) {
    const n = s.pages.length;
    const pages = `${n} ${T(n === 1 ? 'page_one' : 'page_many', n === 1 ? 'page' : 'pages')}`;
    const cut = s.excluded && s.excluded.size ? ` · −${s.excluded.size}` : '';
    if (s.kind === KIND.IMAGE) {
        if (s.crop && s.crop.mm) return fill(T('crop_meta', 'Document :w×:h mm, straightened'), { w: fmtMm(s.crop.mm.w), h: fmtMm(s.crop.mm.h) });
        if (s.crop) return T('crop_meta_fit', 'Document straightened');
        const d = s.dpi ? ` · ${s.dpi} dpi` : '';
        return `${s.px.w}×${s.px.h}${d}`;
    }
    const first = s.pages[0];
    if (first.w == null || first.h == null) return `${pages}${cut} · ${T('size_from_paper', 'size not set in the file')}`;
    return `${pages}${cut} · ${Math.round(first.w)}×${Math.round(first.h)} ${T('unit_mm', 'mm')}`;
}

/* ─────────────────────────── краска ─────────────────────────── */

function renderInk() {
    const c = state.measure;
    const box = $('pdInkResult');
    const sumInk = $('pdSumInk');
    const has = !!(c && c.avg && c.sampled);
    box.hidden = !has;
    if (sumInk) sumInk.textContent = has ? `${fmtPct(c.avg.mono || 0)} %` : '';
    // Инверсия предлагается, только если тёмные листы правда есть (на сборке без неё).
    const dark = state.rawMeasure && state.rawMeasure.m ? state.rawMeasure.m.darkPages.length : (c ? c.darkPages.length : 0);
    $('pdInvertSeg').hidden = !(dark > 0 || state.opts.inkMode === 'invert-dark');
    // «Чистый скан» — там, где есть что чистить.
    const can = hasCleanable();
    $('pdCleanRow').hidden = !can;
    if (!has) return;

    $('pdInkPct').textContent = `${fmtPct(c.avg.mono || 0)} %`;
    $('pdInkRatio').textContent = fill(T('ink_ratio', ':x× a standard ISO page (5%)'), { x: isoRatio(c.avg.mono) });
    const pages = cartridgePages(c.avg.mono, state.opts.cartridgeYield);
    $('pdInkPages').textContent = pages ? fill(T('ink_pages', 'About :n pages from a :y-page cartridge'), { n: pages, y: state.opts.cartridgeYield }) : '';

    const before = state.rawMeasure && state.rawMeasure.m && state.rawMeasure.m.avg;
    const after = $('pdInkAfter');
    const showAfter = state.opts.inkMode !== 'none' && before && Math.abs((before.mono || 0) - (c.avg.mono || 0)) > 0.001;
    after.hidden = !showAfter;
    if (showAfter) after.textContent = fill(T('ink_after', 'Without this mode: :p %'), { p: fmtPct(before.mono || 0) });

    const gs = grayscaleSaving(c.avg);
    const gsEl = $('pdInkGray');
    gsEl.hidden = !(gs && gs.worth);
    if (gs && gs.worth) gsEl.textContent = fill(T('ink_gray', 'Colour is used on this job. Ticking "Grayscale" in the print dialog cuts ink use by about :p%.'), { p: gs.savedPct });
    const darkEl = $('pdInkDark');
    darkEl.hidden = !c.darkPages.length;
    if (c.darkPages.length) darkEl.textContent = fill(T('ink_dark', ':n sheet(s) are mostly dark — they will soak the page in toner.'), { n: c.darkPages.length });
}

/* ─────────────────────────── калибровка краёв ─────────────────────────── */

function openEdgeTest() {
    const m = state.opts.printerMargins;
    for (const [id, k] of [['pdEdgeTop', 'top'], ['pdEdgeBottom', 'bottom'], ['pdEdgeLeft', 'left'], ['pdEdgeRight', 'right']]) {
        const el = $(id);
        if (el) el.value = m && Number.isFinite(+m[k]) ? m[k] : '';
    }
    $('pdEdgeDialog').showModal();
    feat('edge_test_open');
}

async function printEdgeTest() {
    try {
        const paper = sheetSize(state.opts.paper, 'portrait', state.opts.customPaper);
        const bytes = await buildEdgeTest(paper, {
            title: T('edge_title', 'Printer edge test'),
            hint: T('edge_hint', 'Print at 100%. The smallest number you can see is this printer’s limit.'),
            scale: T('ruler_label', ''),
        });
        window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), 'printer-edge-test.pdf');
        feat('edge_test_print');
    } catch (e) {
        window.__handleError(e, 'edge_test_build', { say: T('err_generic', 'Could not build the file') });
    }
}

function applyEdgeTest() {
    const read = (id) => {
        const v = parseFloat(String($(id) && $(id).value).replace(',', '.'));
        return Number.isFinite(v) && v >= 0 && v <= 30 ? v : null;
    };
    const m = { top: read('pdEdgeTop'), bottom: read('pdEdgeBottom'), left: read('pdEdgeLeft'), right: read('pdEdgeRight') };
    /* Полумерка хуже таблицы: либо все четыре, либо ничего. */
    if (Object.values(m).some((v) => v === null)) {
        window.__toast(T('edge_need_all', 'Fill in all four numbers'), 'warning');
        return;
    }
    setOpts({ printerMargins: m });
    $('pdEdgeDialog').close();
    feat('edge_test_answer', m);
    window.__toast(T('edge_saved', 'Saved — the edge margins now come from your own printout.'), 'success');
    renderEdgeProfile();
}

function forgetEdgeTest() {
    setOpts({ printerMargins: null });
    $('pdEdgeDialog').close();
    feat('edge_test_forget');
    renderEdgeProfile();
}

function renderEdgeProfile() {
    const el = $('pdEdgeMeasured');
    const m = state.opts.printerMargins;
    el.hidden = !m;
    if (m) el.textContent = fill(T('edge_measured', 'Measured: :t / :b / :l / :r mm'), { t: m.top, b: m.bottom, l: m.left, r: m.right });
}

/* ─────────────────────────── тест переворота ─────────────────────────── */

function openFlipTest() {
    $('pdFlipDialog').showModal();
    feat('flip_test_open');
}

async function printFlipSide(side) {
    try {
        const paper = sheetSize(state.opts.paper, 'portrait', state.opts.customPaper);
        const bytes = await buildFlipTest(paper, side, {
            front: T('flip_front', 'FRONT'), back: T('flip_back', 'BACK'), topEdge: T('flip_top', 'TOP EDGE OF THE SHEET'),
        });
        window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), `flip-test-${side}.pdf`);
        feat('flip_test_print', { side });
    } catch (e) {
        window.__handleError(e, 'flip_test_build', { say: T('err_generic', 'Could not build the file') });
    }
}

function applyFlipAnswer() {
    const behind = document.querySelector('input[name=pdFlipBehind]:checked');
    const upside = $('pdFlipUpside').checked;
    if (!behind) { window.__toast(T('flip_need_answer', 'Pick what is on the back of sheet 1'), 'warning'); return; }
    const s = flipAnswerToSettings(behind.value, upside);
    state.opts.stackOrder = s.stackOrder;
    state.opts.flip = s.flip;
    state.flipTested = true;
    try { window.__toolSettings.save(SLUG, 'flipAnswer', s); } catch (_) { /* */ }
    $('pdFlipDialog').close();
    feat('flip_test_answer', s);
    window.__toast(T('flip_saved', 'Saved — your printer is now set up for two-sided printing by hand.'), 'success');
    renderModes();
    renderTips();
}

/* ─────────────────────────── вставка текста ─────────────────────────── */

function openPaste(prefill) {
    const d = $('pdPasteDialog');
    const ta = $('pdPasteText');
    if (typeof prefill === 'string') ta.value = prefill;
    d.showModal();
    try { ta.focus({ preventScroll: true }); } catch (_) { ta.focus(); }
    feat('paste_open', { prefilled: prefill ? 1 : 0 });
}

function addPasted() {
    const ta = $('pdPasteText');
    const text = String(ta.value || '');
    if (!text.trim()) { window.__toast(T('paste_empty', 'Type or paste some text first'), 'warning'); return; }
    const sizeEl = document.querySelector('input[name=pdPasteSize]:checked');
    const size = sizeEl ? Number(sizeEl.value) : 11;
    const n = state.sources.filter((s) => s.kind === KIND.TEXT).length + 1;
    const file = new File([text], `${T('paste_name', 'Text')} ${n}.txt`, { type: 'text/plain', lastModified: Date.now() });
    file.__pdTextSize = size;
    $('pdPasteDialog').close();
    ta.value = '';
    addFiles([file], 'paste_text');
}

/* ─────────────────────────── выдача ─────────────────────────── */

function limitKeyForJob() {
    /* Единица — ЗАДАНИЕ, а не файл: «сложить эти файлы в одну печать».
     * Повторный экспорт того же набора после правки полей — бесплатен. */
    const keys = state.sources.filter((s) => s.enabled).map((s) => s.limitKey).sort();
    return keys.length ? `set:${keys.join('|')}` : null;
}

/** Задание с очищенными сканами у не-Премиума: предложить, а не молча снять. */
function cleanNeedsPro() {
    // Где Премиум не продаётся, стены нет и продавать мимо нечего.
    return window.PD_PRO_OFFERED !== false && state.opts.cleanScan && hasCleanable() && !isPro() && !state.noCleanOnce;
}

async function produce(action) {
    if (state.busy) return;
    if (!state.plan || !state.plan.sheets.length) {
        window.__toast(T('err_nothing', 'Add a file first'), 'warning');
        return;
    }
    if (cleanNeedsPro()) {
        $('pdPerk').hidden = false;
        feat('perk_upsell', { what: 'clean_scan', action });
        return;
    }
    const noClean = state.noCleanOnce;
    state.noCleanOnce = false;
    $('pdPerk').hidden = true;

    const key = limitKeyForJob();
    const ticket = window.__toolLimit ? window.__toolLimit.begin(SLUG, key ? [key] : undefined) : { commit() {}, abort() {} };
    if (!ticket) return;                       // стену показал примитив

    const o = state.opts;
    /* Готовые байты предпросмотра — это и есть выдача, когда задание не
     * переставляется (одна копия, без ручного дуплекса) и сборка свежая.
     * Отдаём их сразу — жест нажатия ещё жив, и диалог печати откроется. */
    const reuse = !noClean && state.job && state.job.key === viewKey()
        && (Number(o.copies) || 1) === 1 && !(o.duplex === 'manual' && o.layout !== 'poster');

    setBusy(true, T('busy_building', 'Building the print file…'));
    stage('build_start', { action, sheets: state.plan.sheets.length, reuse: reuse ? 1 : 0 });
    try {
        let bytes, built;
        if (reuse) {
            bytes = state.job.bytes;
            built = { sheets: state.job.sheets, duplex: null };
        } else {
            let dark = null;
            if (o.inkMode === 'invert-dark') dark = (state.job && state.job.darkSheets) || null;
            built = await assemble({ output: true, clean: o.cleanScan && !noClean, darkSheets: dark });
            if (!built) throw new Error('empty_plan');
            bytes = built.bytes;
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        if (blob.size < 900) throw new Error('suspiciously_small');

        state.built = { blob, sheets: built.sheets };
        state.printOk = null;
        let delivered = true;
        if (action === 'print') {
            state.printOk = await tryPrintNow(blob);
        } else if (built.duplex) {
            delivered = await deliverDuplexPair(bytes, built.duplex.fronts.length);
        } else {
            delivered = await window.__downloadFile(blob, outputName());
        }
        if (delivered === false) { ticket.abort(); return; }

        ticket.commit();
        const kinds = state.sources.filter((x) => x.enabled).map((x) => x.kind);
        feat('produced', {
            action, sheets: built.sheets, layout: o.layout,
            duplex: o.duplex, scale: o.scale, ink: o.inkMode,
            kb: Math.round(blob.size / 1024),
            preset: currentPreset(), paper: o.paper, perSheet: o.perSheet,
            pages: state.plan?.stats?.pages || 0,
            files: kinds.length, kinds: [...new Set(kinds)].sort().join('+'),
            ruler: o.ruler ? (state.plan?.rulerRoom === false ? 'no_room' : 'on') : 'off',
            clean: o.cleanScan && !noClean ? 1 : 0, fix: scaleFix(o) !== 1 ? 1 : 0, reuse: reuse ? 1 : 0,
        });
        try { window.__aeToolComplete && window.__aeToolComplete({ slug: SLUG, sheets: built.sheets }); } catch (_) { /* */ }
        showAfter(action);
    } catch (e) {
        ticket.abort();
        diag('build_fail', { message: String(e && e.message).slice(0, 140) });
        window.__handleError(e, 'print_documents_build', { say: T('err_build', 'Could not build the print file. Try fewer files or reload the page.') });
    } finally {
        setBusy(false);
    }
}

/**
 * Ручной дуплекс отдаётся ДВУМЯ файлами — лицо и оборот: два набора номеров,
 * набираемые вручную между проходами, — ровно то место, где ошибаются.
 * ⚠️ Через `__batchDeliver`, а не двумя `__downloadFile` подряд (один файл на жест).
 */
async function deliverDuplexPair(bytes, n) {
    const { PDFDocument } = await loadPdfLib();
    const whole = await PDFDocument.load(bytes);
    const cut = async (from, to) => {
        const doc = await PDFDocument.create();
        const idx = [];
        for (let i = from; i < to; i++) idx.push(i);
        const pages = await doc.copyPages(whole, idx);
        for (const p of pages) doc.addPage(p);
        return new Blob([await doc.save()], { type: 'application/pdf' });
    };
    const base = outputName().replace(/\.pdf$/i, '');
    const items = [
        { name: `${base}-1-${T('dup_fronts', 'fronts')}.pdf`, blob: await cut(0, n) },
        { name: `${base}-2-${T('dup_backs', 'backs')}.pdf`, blob: await cut(n, whole.getPageCount()) },
    ];
    if (!window.__batchDeliver) {
        await window.__downloadFile(items[0].blob, items[0].name);
        window.__toast(T('dup_one_only', 'Only the fronts were saved — reload the page and try again.'), 'warning');
        return true;
    }
    const res = await window.__batchDeliver.deliver(items, { zipName: `${base}.zip`, slug: SLUG });
    feat('duplex_pair', { via: res.via, written: res.written });
    if (res.via === 'declined' || res.via === 'refused') {
        window.__toast(T('dup_declined', 'Saving was cancelled.'), 'warning');
        return false;
    }
    window.__toast(T('dup_saved', 'Two files saved: print the first one, flip the stack, print the second.'), 'success');
    return true;
}

function outputName() {
    const base = state.sources.length === 1
        ? String(state.sources[0].name).replace(/\.[^.]+$/, '')
        : T('out_base', 'print-job');
    const o = state.opts;
    const bits = [];
    if (o.layout === 'nup') bits.push(`${o.perSheet}up`);
    if (o.layout === 'booklet') bits.push('booklet');
    if (o.layout === 'poster') bits.push('poster');
    if (o.duplex === 'manual') bits.push('duplex');
    return window.__outputName
        ? window.__outputName.join([base].concat(bits), '-', 'pdf')
        : `${base}${bits.length ? '-' + bits.join('-') : ''}.pdf`;
}

/*
 * Открыть диалог печати — ЛЕЙН ВЫБИРАЕТСЯ ЗАМЕРОМ, а не надеждой
 * (замер 08.09.2026 в трёх движках, память reference_a_hidden_iframe_prints_a_blank_page):
 *   • WebKit и Firefox без смотрелки PDF в кадре печатают ЧИСТЫЙ ЛИСТ;
 *   • Chromium отдаёт кадр смотрелке, и он становится cross-origin;
 *   • активация жеста живёт секунды — после сборки её может уже не быть.
 * Правила: печатать кадр, только если в нём ДОКАЗАННО PDF; без жеста — не
 * открывать молча, а показать кнопку, нажатие по которой вернёт активацию.
 * С 23.10.2026 байты чаще всего уже готовы к нажатию (см. produce/reuse), и
 * жест доживает до print() — запасная кнопка нужна реже.
 */

/** Что на самом деле лежит в кадре: 'pdf' | 'blank' | 'viewer' (cross-origin). */
function frameHoldsPdf(frame) {
    let doc = null;
    try { doc = frame.contentDocument; } catch (_) { return 'viewer'; }
    if (!doc) return 'viewer';
    const type = String(doc.contentType || '').toLowerCase();
    if (type.includes('pdf')) return 'pdf';
    if (doc.querySelector('embed, object, canvas')) return 'pdf';
    const body = doc.body;
    if (!body || (body.children.length === 0 && !String(body.innerHTML || '').trim())) return 'blank';
    return 'pdf';
}

const gestureAlive = () => {
    try { return navigator.userActivation ? navigator.userActivation.isActive !== false : true; }
    catch (_) { return true; }
};

async function tryPrintNow(blob) {
    const url = URL.createObjectURL(blob);
    const frame = document.createElement('iframe');
    frame.className = 'pd-print-frame';
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.src = url;
    document.body.appendChild(frame);
    const cleanup = () => { setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 120000); };

    await new Promise((resolve) => {
        let done = false;
        const fin = () => { if (!done) { done = true; resolve(); } };
        frame.onload = fin;
        setTimeout(fin, 2500);
    });

    const holds = frameHoldsPdf(frame);
    if (holds === 'blank') {
        feat('print_dialog', { via: 'blank_frame', ok: 0 });
        cleanup();
        return false;
    }
    try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        feat('print_dialog', { via: 'iframe', ok: 1 });
        cleanup();
        return true;
    } catch (_) {
        if (gestureAlive()) {
            const w = window.open(url, '_blank');
            feat('print_dialog', { via: 'window_open', ok: w ? 1 : 0 });
            cleanup();
            return !!w;
        }
        feat('print_dialog', { via: 'no_gesture', ok: 0 });
        cleanup();
        return false;
    }
}

/** Ручной лейн: вызывается ИЗ СВЕЖЕГО нажатия, поэтому активация есть. */
async function printFromGesture() {
    if (!state.built) return;
    /* На айфоне «Поделиться» — это и есть путь к AirPrint: системный лист
     * несёт пункт «Печать». */
    try {
        const file = new File([state.built.blob], outputName(), { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: T('job_title', 'Print') });
            feat('print_dialog', { via: 'share', ok: 1 });
            return;
        }
    } catch (e) {
        if (e && e.name === 'AbortError') { feat('print_dialog', { via: 'share', ok: 0 }); return; }
    }
    const ok = await tryPrintNow(state.built.blob);
    if (!ok) window.__toast(T('print_no_dialog', 'Could not open the print dialog'), 'warning');
}

function showAfter(action) {
    $('pdAfter').hidden = false;
    $('pdPrintReady').hidden = !(action === 'print' && state.built && state.printOk === false);
    $('pdPrintFallback').hidden = !(action === 'print' && state.built && state.printOk === true);
}

function saveBuilt() {
    if (!state.built) return;
    window.__downloadFile(state.built.blob, outputName());
    feat('print_fallback_download');
}

/* ─────────────────────────── интерфейс ─────────────────────────── */

function setBusy(on, label) {
    state.busy = !!on;
    for (const b of document.querySelectorAll('[data-produce]')) b.disabled = !!on || !(state.plan && state.plan.sheets.length);
    const btn = $('pdGoPrint');
    if (btn) btn.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on && label) setStageBusy(true, label);
    else if (!on) setStageBusy(false);
}

function setStageBusy(on, label, progress) {
    const el = $('pdStageBusy');
    if (!el) return;
    el.hidden = !on;
    if (on) {
        $('pdBusyLabel').textContent = label || '';
        const bar = $('pdBusyBar');
        bar.hidden = !(progress > 0);
        bar.style.width = `${Math.round((progress || 0) * 100)}%`;
    }
}

function setDeskState() {
    const desk = $('pdDesk');
    const ready = state.sources.length > 0;
    desk.dataset.state = ready ? 'ready' : 'empty';
    $('pdStage').hidden = !ready;
}

/** Одна или несколько настроек — за один пересчёт. */
function setOpts(patch, { silent = false } = {}) {
    const o = state.opts;
    Object.assign(o, patch);
    normalize(o, patch);
    if (!silent) {
        for (const k of Object.keys(patch)) {
            if (PERSIST.includes(k)) { try { window.__toolSettings.save(SLUG, k, o[k]); } catch (_) { /* */ } }
        }
    }
    syncControls();
    recompute();
}

/** Согласовать оси после изменения: каждая раскладка понимает свой масштаб. */
function normalize(o, patch) {
    if ('layout' in patch) {
        if (o.layout === 'nup' && !['fit', 'actual', 'custom'].includes(o.scale)) o.scale = 'fit';
        if (o.layout === 'nup' && !(o.perSheet > 1)) o.perSheet = 2;
        if (o.layout === 'single') o.perSheet = 1;
        if ((o.layout === 'poster' || o.layout === 'booklet') && o.marginPreset === 'none') o.marginPreset = 'narrow';
        // Книжка — всегда с двух сторон: «Нет» для неё не значит ничего.
        if (o.layout === 'booklet' && o.duplex === 'off') o.duplex = 'auto';
    }
    if (o.layout === 'poster' && o.duplex !== 'off' && 'layout' in patch) o.duplex = 'off';
    o.copies = Math.max(1, Math.min(COPIES_MAX, Math.round(Number(o.copies) || 1)));
}

/** Какой пресет ДЕЙСТВИТЕЛЬНО в силе — ярлык для телеметрии, выведенный из настроек. */
function currentPreset() {
    for (const [name, p] of Object.entries(PRESETS)) {
        if (Object.keys(p).every((k) => state.opts[k] === p[k])) return name;
    }
    return null;
}

function syncControls() {
    const o = state.opts;
    for (const el of document.querySelectorAll('[data-seg]')) {
        const [key, val] = el.dataset.seg.split(':');
        let on = String(o[key]) === val;
        // У n-up «Вписать» — и «уменьшить, если надо», и «заполнить»: в ячейке это одно.
        if (key === 'scale' && el.closest('#pdOptNup')) on = val === 'fit' ? (o.scale === 'fit' || o.scale === 'shrink') : on;
        el.classList.toggle('on', on);
        if (el.getAttribute('role') === 'radio') el.setAttribute('aria-checked', on ? 'true' : 'false');
        else el.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    const nested = {
        customW: () => (o.customPaper || {}).w, customH: () => (o.customPaper || {}).h,
        mTop: () => (o.margins || {}).top, mRight: () => (o.margins || {}).right,
        mBottom: () => (o.margins || {}).bottom, mLeft: () => (o.margins || {}).left,
    };
    for (const el of document.querySelectorAll('[data-bind]')) {
        const key = el.dataset.bind;
        const v = nested[key] ? nested[key]() : o[key];
        if (el.type === 'checkbox') el.checked = !!v;
        else if (document.activeElement !== el) el.value = v == null ? '' : v;
    }
    const lay = o.layout;
    show('pdOptSingle', lay === 'single');
    show('pdOptNup', lay === 'nup');
    show('pdOptBooklet', lay === 'booklet');
    show('pdOptPoster', lay === 'poster');
    show('pdCustomScale', o.scale === 'custom' && (lay === 'single' || lay === 'nup'));
    show('pdCustomMargins', o.marginPreset === 'custom');
    show('pdCustomPaper', o.paper === 'custom');
    show('pdDuplexRow', o.duplex === 'manual');
    show('pdLightenRow', o.inkMode === 'lighten');
    const fs = $('pdFlipState');
    if (fs) fs.textContent = state.flipTested ? T('flip_state_ok', 'Printer set up ✓') : T('flip_state_no', 'Not set up yet');
    const ss = $('pdScaleNote');
    if (ss) ss.textContent = T(`scale_note_${o.scale}`, '');
    const sp = $('pdScaleFixNote');
    if (sp) {
        const k = scaleFix(o);
        sp.textContent = Math.abs(k - 1) > 0.0005 ? fill(T('scalefix_on', 'Every sheet is enlarged ×:k — your driver shrinks it back to 1:1.'), { k: k.toFixed(3) }) : T('scalefix_off', '');
    }
    document.getElementById('pdApp').dataset.layout = lay;
}

/** Подписи, зависящие от плана (размер плаката, страницы). */
function renderModes() {
    const o = state.opts;
    const info = $('pdPosterInfo');
    if (info) {
        const p = state.plan;
        if (p && o.layout === 'poster' && p.sheets.length) {
            const sh = p.sheets[0];
            const sl = sh.slots[0];
            info.textContent = fill(T('poster_info', ':cols×:rows sheets · :w×:h cm'), {
                cols: sh.poster.cols, rows: sh.poster.rows,
                w: Math.round(sl.w / MM / 10), h: Math.round(sl.h / MM / 10),
            });
        } else info.textContent = '';
    }
    const sp = $('pdSumPages');
    if (sp) {
        const bits = [];
        if (o.ranges) bits.push(o.ranges);
        if (o.parity !== 'all') bits.push(T(`parity_${o.parity}`, o.parity));
        if (o.reverse) bits.push('↺');
        sp.textContent = bits.length ? bits.join(' · ') : T('pages_all', 'all');
    }
}

function show(id, on) {
    const el = $(id);
    if (el) el.hidden = !on;
}

/* ─────────────────────────── копия документа ─────────────────────────── */

const fmtMm = (v) => {
    try { return new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : undefined, { maximumFractionDigits: 1 }).format(v); } catch (_) { return String(v); }
};

/** Найти документ на фото — фоном, после загрузки. Результат — подсказка, а не обрезка. */
async function findDocument(src) {
    if (src.kind !== KIND.IMAGE || src.docProbe) return;
    src.docProbe = true;
    try {
        const img = await src.sample(320);
        const r = detectDocument(img);
        src.docFound = r && r.confidence >= 0.45 ? r : null;
        feat('doc_detect', { found: src.docFound ? 1 : 0, conf: r ? r.confidence : 0 });
        if (src.docFound) renderTips();
    } catch (e) {
        diag('doc_detect_fail', { message: String(e && e.message).slice(0, 100) });
    }
}

/** Фото документа, которое ещё не обрезано, — для подсказки. */
function docPhotoCandidate() {
    const i = state.sources.findIndex((s) => s.enabled && s.kind === KIND.IMAGE && s.docFound && !s.crop);
    return i < 0 ? null : { src: i, name: state.sources[i].name };
}

const cropUi = { src: -1, quad: null, type: 'passport_spread', bmp: null };

async function openCrop(i, via) {
    const s = state.sources[i];
    if (!s || s.kind !== KIND.IMAGE) return;
    cropUi.src = i;
    const found = s.docFound;
    cropUi.quad = s.crop ? s.crop.quad.map((p) => p.slice())
        : (found ? found.quad.map((p) => p.slice()) : [[0.06, 0.06], [0.94, 0.06], [0.94, 0.94], [0.06, 0.94]]);
    cropUi.type = s.crop ? s.crop.type : guessType(quadAspect(cropUi.quad, s.px.w, s.px.h));
    $('pdCropHint').textContent = s.crop || found
        ? T('crop_hint_found', 'The corners are placed on the document — drag them if they are off.')
        : T('crop_hint_none', 'Drag the four corners onto the corners of the document.');
    $('pdCropReset').hidden = !s.crop;
    if (s.crop && s.crop.type === 'custom' && s.crop.mm) { $('pdCropW').value = s.crop.mm.w; $('pdCropH').value = s.crop.mm.h; }
    $('pdCropDialog').showModal();
    feat('crop_open', { via, found: found ? 1 : 0 });
    syncCropUi();
    try {
        if (cropUi.bmp) { try { cropUi.bmp.close(); } catch (_) { /* */ } cropUi.bmp = null; }
        cropUi.bmp = await s.previewBitmap();
        drawCropCanvas();
    } catch (e) {
        window.__handleError(e, 'print_documents_crop_open', { say: T('err_generic', 'Could not open this file') });
    }
}

function drawCropCanvas() {
    const c = $('pdCropCanvas'), bmp = cropUi.bmp;
    if (!c || !bmp) return;
    const stageEl = $('pdCropStage');
    const maxW = Math.max(200, stageEl.clientWidth || 600);
    const maxH = Math.max(200, Math.round(window.innerHeight * 0.56));
    const k = Math.min(maxW / bmp.width, maxH / bmp.height, 1);
    const cssW = Math.round(bmp.width * k), cssH = Math.round(bmp.height * k);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.round(cssW * dpr); c.height = Math.round(cssH * dpr);
    const frame = $('pdCropFrame');
    frame.style.width = `${cssW}px`;
    frame.style.height = `${cssH}px`;
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    placeHandles();
}

function placeHandles() {
    const q = cropUi.quad;
    document.querySelectorAll('.pd-crop-h').forEach((h) => {
        const [x, y] = q[Number(h.dataset.corner)];
        h.style.left = `${(x * 100).toFixed(3)}%`;
        h.style.top = `${(y * 100).toFixed(3)}%`;
    });
    const pts = q.map(([x, y]) => `${(x * 100).toFixed(3)},${(y * 100).toFixed(3)}`).join(' ');
    $('pdCropPoly').setAttribute('points', pts);
    $('pdCropShade').setAttribute('d', `M0 0H100V100H0Z M${q.map(([x, y]) => `${(x * 100).toFixed(3)} ${(y * 100).toFixed(3)}`).join(' L')} Z`);
}

function syncCropUi() {
    for (const b of document.querySelectorAll('[data-doc]')) {
        const on = b.dataset.doc === cropUi.type;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    }
    $('pdCropCustom').hidden = cropUi.type !== 'custom';
    const s = state.sources[cropUi.src];
    const sizeEl = $('pdCropSize');
    if (s && sizeEl) {
        const mm = copySizeMm(cropUi.type, quadAspect(cropUi.quad, s.px.w, s.px.h), customMm());
        sizeEl.textContent = mm
            ? fill(T('crop_size', 'Prints at :w×:h mm'), { w: fmtMm(mm.w), h: fmtMm(mm.h) })
            : T('crop_size_fit', 'Prints fitted to the sheet');
    }
}

function customMm() {
    const w = parseFloat(String($('pdCropW').value).replace(',', '.'));
    const h = parseFloat(String($('pdCropH').value).replace(',', '.'));
    return w > 0 && h > 0 ? { w, h } : null;
}

function applyCrop() {
    const s = state.sources[cropUi.src];
    if (!s) return;
    const aspect = quadAspect(cropUi.quad, s.px.w, s.px.h);
    const mm = copySizeMm(cropUi.type, aspect, customMm());
    if (cropUi.type === 'custom' && !mm) { window.__toast(T('crop_need_size', 'Enter the document size in millimeters'), 'warning'); return; }
    s.setCrop({ quad: cropUi.quad.map((p) => p.slice()), type: cropUi.type, mm });
    $('pdCropDialog').close();
    feat('crop_apply', { type: cropUi.type, found: s.docFound ? 1 : 0 });
    /* Все документы задания — обрезанные копии в настоящем размере: это и есть
     * «копия на одном листе». Укладываем их по 2 или 4 на лист сразу; одна
     * копия остаётся одна — тоже 1:1. */
    const docs = state.sources.filter((x) => x.enabled);
    const allDocs = docs.length > 0 && docs.every((x) => x.kind === KIND.IMAGE && x.crop && x.crop.mm);
    if (allDocs && (state.opts.layout === 'single' || state.opts.layout === 'nup')) {
        const n = docs.length;
        if (n >= 2) {
            const per = n > 2 ? 4 : 2;
            setOpts({ layout: 'nup', perSheet: per, scale: 'actual', marginPreset: 'narrow' });
            window.__toast(fill(T('crop_packed', 'Documents placed :n per sheet at actual size'), { n: per }), 'success');
            feat('idcopy_pack', { n });
        } else {
            setOpts({ layout: 'single', scale: 'actual', marginPreset: 'narrow' });
        }
    }
    renderFiles();
    recompute({ now: true });
}

function resetCrop() {
    const s = state.sources[cropUi.src];
    if (!s) return;
    s.setCrop(null);
    $('pdCropDialog').close();
    feat('crop_reset');
    renderFiles();
    recompute({ now: true });
}

/** Углы двигаются пальцем, мышью и стрелками клавиатуры (Shift — крупный шаг). */
function wireCrop() {
    const frame = $('pdCropFrame');
    if (!frame) return;
    let drag = null;
    frame.addEventListener('pointerdown', (e) => {
        const h = e.target.closest('.pd-crop-h');
        if (!h) return;
        e.preventDefault();
        drag = { k: Number(h.dataset.corner), id: e.pointerId };
        try { h.setPointerCapture(e.pointerId); } catch (_) { /* */ }
        h.classList.add('is-drag');
    });
    frame.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const r = frame.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
        const y = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
        cropUi.quad[drag.k] = [x, y];
        placeHandles();
        syncCropUi();
    });
    const end = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const h = frame.querySelector(`.pd-crop-h[data-corner="${drag.k}"]`);
        if (h) h.classList.remove('is-drag');
        drag = null;
    };
    frame.addEventListener('pointerup', end);
    frame.addEventListener('pointercancel', end);
    frame.addEventListener('keydown', (e) => {
        const h = e.target.closest('.pd-crop-h');
        if (!h) return;
        const step = e.shiftKey ? 0.02 : 0.004;
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (!d) return;
        e.preventDefault();
        const k = Number(h.dataset.corner);
        const [x, y] = cropUi.quad[k];
        cropUi.quad[k] = [Math.max(0, Math.min(1, x + d[0])), Math.max(0, Math.min(1, y + d[1]))];
        placeHandles();
        syncCropUi();
    });
    $('pdCropW').addEventListener('input', syncCropUi);
    $('pdCropH').addEventListener('input', syncCropUi);
    window.addEventListener('resize', () => { if ($('pdCropDialog').open) drawCropCanvas(); });
    $('pdCropDialog').addEventListener('close', () => {
        if (cropUi.bmp) { try { cropUi.bmp.close(); } catch (_) { /* */ } cropUi.bmp = null; }
    });
}

/* ─────────────────────────── просмотрщик ─────────────────────────── */

let viewer = null;

function onViewCount(total, idx) {
    const c = $('pdCount');
    if (c) c.textContent = total ? fill(T('view_count', ':i / :n'), { i: idx + 1, n: total }) : '';
    $('pdPrev').disabled = idx <= 0;
    $('pdNext').disabled = idx >= total - 1;
}

/* ─────────────────────────── запуск ─────────────────────────── */

function wireStepper(btn) {
    const [key, d] = btn.dataset.step.split(':');
    const delta = Number(d);
    const input = document.querySelector(`[data-bind="${key}"]`);
    const step = () => {
        const min = input ? Number(input.min) : -Infinity, max = input ? Number(input.max) : Infinity;
        const cur = Number(state.opts[key]) || 0;
        const next = Math.max(min, Math.min(max, cur + delta));
        if (next === cur) return false;
        setOpts({ [key]: next });
        return true;
    };
    // Клавиатура (Enter/Пробел) — обычным click; нажатие пальцем/мышью с
    // удержанием — общим примитивом повтора (его click того же нажатия гасится).
    btn.addEventListener('click', step);
    if (window.__holdRepeat) window.__holdRepeat(btn, step, { delay: 400, period: 90 });
}

function wire() {
    /* Через `__bindUploaderFile`: модуль отложенный, и файл, выбранный раньше
     * подписки, зона отдаёт опоздавшему (UploaderListenerRaceTest). */
    for (const zone of document.querySelectorAll('.tool-uploader')) {
        window.__bindUploaderFile(zone, (file, detail) => {
            const list = (detail && detail.files) || (file ? [file] : null);
            if (list && list.length) addFiles(list, 'zone');
        });
        // × на свёрнутой строке зоны — «убрать всё».
        zone.addEventListener('tool-uploader:clear', () => { if (state.sources.length) { feat('files_cleared'); clearAll(); } });
    }
    const input = $('pdInput');
    input.addEventListener('change', () => { addFiles(input.files, 'add_more'); input.value = ''; });

    document.addEventListener('click', (e) => {
        const t = e.target;
        const seg = t.closest('[data-seg]');
        if (seg && seg.closest('#pdApp')) {
            const [key, val] = seg.dataset.seg.split(':');
            const num = /^-?\d+(\.\d+)?$/.test(val) ? Number(val) : val;
            if (key === 'layout') feat('layout', { to: val });
            setOpts({ [key]: num });
            return;
        }
        const prod = t.closest('[data-produce]');
        if (prod) { produce(prod.dataset.produce); return; }
        const drop = t.closest('[data-drop]');
        if (drop) {
            const i = Number(drop.dataset.drop);
            const s = state.sources[i];
            if (s && s.close) s.close();
            state.sources.splice(i, 1);
            renderFiles();
            syncZone();
            recompute();
            feat('file_removed');
            return;
        }
        const cropBtn = t.closest('[data-crop]');
        if (cropBtn) { openCrop(Number(cropBtn.dataset.crop), 'row'); return; }
        const docBtn = t.closest('[data-doc]');
        if (docBtn) { cropUi.type = docBtn.dataset.doc; syncCropUi(); return; }
        const move = t.closest('[data-move]');
        if (move) {
            const [i, d] = move.dataset.move.split(':').map(Number);
            const j = i + d;
            if (j >= 0 && j < state.sources.length) {
                const x = state.sources[i];
                state.sources[i] = state.sources[j];
                state.sources[j] = x;
                renderFiles();
                recompute();
                const btn = document.querySelector(`[data-move="${j}:${d}"]`);
                if (btn && !btn.disabled) btn.focus({ preventScroll: true });
            }
            return;
        }
        const id = (t.closest('button') || {}).id;
        switch (id) {
            case 'pdDropMore':
            case 'pdAddMore': {
                const z = zoneEl();
                if (z && typeof z.__uploaderOpen === 'function') z.__uploaderOpen(); else input.click();
                return;
            }
            case 'pdPasteOpen': openPaste(); return;
            case 'pdPasteAdd': addPasted(); return;
            case 'pdPasteClose': $('pdPasteDialog').close(); return;
            case 'pdSaveBuilt': case 'pdSaveBuiltAlt': saveBuilt(); return;
            case 'pdPrintNow': printFromGesture(); return;
            case 'pdEdgeOpen': openEdgeTest(); return;
            case 'pdEdgeGet': printEdgeTest(); return;
            case 'pdEdgeApply': applyEdgeTest(); return;
            case 'pdEdgeForget': forgetEdgeTest(); return;
            case 'pdEdgeClose': $('pdEdgeDialog').close(); return;
            case 'pdFlipOpen': openFlipTest(); return;
            case 'pdFlipFront': printFlipSide('front'); return;
            case 'pdFlipBack': printFlipSide('back'); return;
            case 'pdFlipApply': applyFlipAnswer(); return;
            case 'pdFlipClose': $('pdFlipDialog').close(); return;
            case 'pdRestorePages':
                for (const s2 of state.sources) if (s2.excluded) s2.excluded.clear();
                feat('sheets_restored');
                renderFiles();
                recompute();
                return;
            case 'pdZoomIn': viewer.zoomIn(); feat('zoom', { z: 'in' }); return;
            case 'pdZoomOut': viewer.zoomOut(); feat('zoom', { z: 'out' }); return;
            case 'pdZoomFit': viewer.zoomFit(); return;
            case 'pdPrev': viewer.goTo(viewer.currentIndex() - 1); return;
            case 'pdNext': viewer.goTo(viewer.currentIndex() + 1); return;
            case 'pdZoneToggle': {
                const b = $('pdZoneToggle');
                const on = b.getAttribute('aria-pressed') !== 'true';
                b.setAttribute('aria-pressed', on ? 'true' : 'false');
                viewer.setZone(on);
                try { window.__toolSettings.save(SLUG, 'viewZone', on); } catch (_) { /* */ }
                feat('zone_toggle', { on: on ? 1 : 0 });
                return;
            }
            case 'pdPerkPlain':
                state.noCleanOnce = true;
                $('pdPerk').hidden = true;
                feat('perk_declined', { what: 'clean_scan' });
                return;
            case 'pdPerkClose': $('pdPerk').hidden = true; return;
            case 'pdCropApply': applyCrop(); return;
            case 'pdCropReset': resetCrop(); return;
            case 'pdCropClose': $('pdCropDialog').close(); return;
            default: break;
        }
    });

    document.addEventListener('input', (e) => {
        const range = e.target.closest('[data-range]');
        if (range) {
            const s = state.sources[Number(range.dataset.range)];
            if (s) s.range = range.value;
            recompute();
            return;
        }
        const bind = e.target.closest('[data-bind]');
        if (bind && bind.closest('#pdApp')) {
            const key = bind.dataset.bind;
            let v = bind.type === 'checkbox' ? bind.checked : bind.value;
            if (bind.type === 'number' || bind.type === 'range') {
                v = Number(String(bind.value).replace(',', '.'));
                if (!Number.isFinite(v)) return;
            }
            if (key === 'customW' || key === 'customH') {
                const cp = { ...(state.opts.customPaper || { w: 210, h: 297 }) };
                cp[key === 'customW' ? 'w' : 'h'] = v;
                setOpts({ customPaper: cp });
                return;
            }
            if (/^m(Top|Right|Bottom|Left)$/.test(key)) {
                const m = { ...(state.opts.margins || { top: 12, right: 12, bottom: 12, left: 12 }) };
                m[key.slice(1).toLowerCase()] = v;
                setOpts({ margins: m });
                return;
            }
            if (key === 'cleanScan') feat('clean_toggle', { on: v ? 1 : 0, via: 'switch' });
            if (key === 'printerScale') feat('printer_scale', { v });
            setOpts({ [key]: v });
        }
    });

    for (const b of document.querySelectorAll('[data-step]')) wireStepper(b);

    /* Закрыть диалог щелчком мимо него. Там, где браузер умеет `closedby`,
     * это делает он сам; здесь — та же логика для остальных. */
    for (const d of document.querySelectorAll('.pd-dialog')) {
        d.addEventListener('click', (e) => {
            if (e.target !== d) return;
            const r = d.getBoundingClientRect();
            const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
            if (!inside) d.close();
        });
    }

    /* ── вставка из буфера (Ctrl+V / ⌘V) ─────────────────────────────────
     * Скриншот, файл, скопированный в проводнике, или просто текст — сразу
     * в печать. Внутри полей ввода вставка остаётся вставкой в поле. */
    document.addEventListener('paste', (e) => {
        const a = document.activeElement;
        if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return;
        const dt = e.clipboardData;
        if (!dt) return;
        const files = [...(dt.files || [])].filter(Boolean);
        if (files.length) {
            e.preventDefault();
            const named = files.map((f, i) => (f.name && f.name !== 'image.png' ? f
                : new File([f], `${T('paste_image', 'Pasted image')} ${i + 1}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type, lastModified: Date.now() })));
            injectFiles(named, 'paste');
            return;
        }
        const text = dt.getData('text/plain');
        if (text && text.trim()) { e.preventDefault(); openPaste(text); }
    });

    /* ── бросок файлов на любую часть стола ──────────────────────────────
     * Зона загрузки после первого файла сворачивается в строку, и попасть в
     * неё ещё одним файлом трудно. Стол принимает бросок целиком. */
    const desk = $('pdDesk');
    let dragDepth = 0;
    const hasFiles = (e) => e.dataTransfer && [...(e.dataTransfer.types || [])].includes('Files');
    desk.addEventListener('dragenter', (e) => {
        if (!hasFiles(e) || e.target.closest('.tool-uploader')) return;
        dragDepth++;
        desk.classList.add('is-drop');
    });
    desk.addEventListener('dragleave', () => { dragDepth = Math.max(0, dragDepth - 1); if (!dragDepth) desk.classList.remove('is-drop'); });
    desk.addEventListener('dragover', (e) => { if (hasFiles(e) && !e.target.closest('.tool-uploader')) e.preventDefault(); });
    desk.addEventListener('drop', (e) => {
        dragDepth = 0;
        desk.classList.remove('is-drop');
        if (!hasFiles(e) || e.target.closest('.tool-uploader')) return;
        e.preventDefault();
        injectFiles([...e.dataTransfer.files], 'drop');
    });

    // Во весь экран — сцена с листами.
    const whole = $('pdWhole');
    if (typeof window.__wholeScreen === 'function') {
        const ctl = window.__wholeScreen($('pdStage'), {
            key: 'print-documents',
            onChange(on) {
                whole.setAttribute('aria-pressed', on ? 'true' : 'false');
                const t = on ? T('view_whole_exit', 'Exit full screen') : T('view_whole', 'Full screen');
                whole.title = t; whole.setAttribute('aria-label', t);
                requestAnimationFrame(() => viewer.layout());
            },
        });
        whole.addEventListener('click', () => { ctl.toggle(); feat('view_whole'); });
    } else whole.hidden = true;
}

function restore() {
    try {
        for (const key of PERSIST) {
            const v = window.__toolSettings.load(SLUG, key, undefined);
            if (v !== undefined && v !== null) state.opts[key] = v;
        }
        /* Ответ теста переворота. До 23.10.2026 он лежал под ключом `duplex` —
         * тем же, что выбор «С двух сторон»; читаем и старое место. */
        let d = window.__toolSettings.load(SLUG, 'flipAnswer', null);
        if (!d) {
            const old = window.__toolSettings.load(SLUG, 'duplex', null);
            if (old && typeof old === 'object' && old.stackOrder) d = old;
        }
        if (d && d.stackOrder) {
            state.opts.stackOrder = d.stackOrder;
            state.opts.flip = d.flip;
            state.flipTested = true;
        }
        const z = window.__toolSettings.load(SLUG, 'viewZone', false);
        if (z === true) { $('pdZoneToggle').setAttribute('aria-pressed', 'true'); viewer.setZone(true); }
    } catch (_) { /* */ }
}

export function boot() {
    viewer = createViewer({
        root: $('pdPreview'), scroller: $('pdScroller'), T,
        onDrop(src, page) {
            const s = state.sources[src];
            if (!s) return;
            (s.excluded || (s.excluded = new Set())).add(page);
            feat('sheet_dropped', { left: activePages().length });
            renderFiles();
            recompute();
        },
        onCount: onViewCount,
    });
    restore();
    wire();
    wireCrop();
    syncControls();
    renderEdgeProfile();
    renderFiles();
    renderSummary();
    stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
